import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { verifyPin } from "@/api/auth";

const Verify2FA = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState<string>("");

  useEffect(() => {
    // Get the pending username from localStorage
    const pendingUsername = localStorage.getItem("pendingUsername");
    if (!pendingUsername) {
      // No pending login, redirect to login page
      navigate("/login");
      return;
    }
    setUsername(pendingUsername);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 6 || !username) return;

    setError(null);
    setLoading(true);
    try {
      const res = await verifyPin({ username, pin });
      if (res?.isSuccess) {
        // Store admin status from backend
        if (res.isAdmin !== undefined) {
          localStorage.setItem("isAdmin", res.isAdmin.toString());
        }
        // Store user's IK for patient association
        if (res.ik) {
          localStorage.setItem("userIk", res.ik);
        }
        // Store hospital name
        if (res.kundeName) {
          localStorage.setItem("krankenhausName", res.kundeName);
        }
        // Clear pending username
        localStorage.removeItem("pendingUsername");
        // Navigate to patients page
        navigate("/patients");
      } else {
        setError(res?.message ?? "PIN-Verifizierung fehlgeschlagen");
      }
    } catch (err: any) {
      setError(err?.message ?? "Fehler bei der PIN-Verifizierung");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
              Zwei-Faktor-Authentifizierung
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bitte geben Sie den 6-stelligen PIN ein, den Sie per E-Mail erhalten haben
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                id="pin"
                label="PIN-Code"
                type="text"
                placeholder="123456"
                inputProps={{ maxLength: 6 }}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                required
                fullWidth
                sx={{
                  "& input": {
                    textAlign: "center",
                    fontSize: "2rem",
                    letterSpacing: "0.5rem",
                  },
                }}
              />

              {error && (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={pin.length !== 6 || loading}
                sx={{ color: "white" }}
              >
                {loading ? "Verifizierung läuft..." : "Verifizieren"}
              </Button>

              <Button
                variant="text"
                size="large"
                fullWidth
                onClick={() => {
                  localStorage.removeItem("pendingUsername");
                  navigate("/login");
                }}
              >
                Zurück zur Anmeldung
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Verify2FA;
